import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
import { API_VERSION } from "../types";
import { AgeVerificationFlow } from "./types";
const API_PATH = "users/age-verification";
// Call on app bootstrap and on every page load to ensure the UI is always in sync with the user's current permissions.
export const getAgeVerificationStatus = createAsyncThunk("ageVerification/status", async (params, thunkAPI) => {
    try {
        let apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH}/status`;
        if (params?.triggerError) {
            apiUrl += "?triggerError=true";
        }
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
// Triggered when the user needs to start the verification flow. The backend returns an ordered list of methods.
export const getAgeVerificationMethods = createAsyncThunk("ageVerification/methods", async (params, thunkAPI) => {
    try {
        let apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH}/methods`;
        if (params?.priority) {
            const encodedPriority = encodeURIComponent(params.priority);
            apiUrl += `?priority=${encodedPriority}`;
        }
        const response = await axiosClient.get(apiUrl);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
// Triggered when the user needs to start the verification flow. The backend returns an ordered list of methods.
export const postAgeVerificationInitialize = createAsyncThunk("ageVerification/initialize", async (params, thunkAPI) => {
    try {
        const { redirectScheme, ...requestBody } = params;
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH}/initialize`;
        const response = await axiosClient.post(apiUrl, requestBody, redirectScheme
            ? { headers: { "x-redirect-scheme": redirectScheme } }
            : undefined);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
// Called from the frontend's callback page after the provider redirects the user back.
export const postAgeVerificationFinalize = createAsyncThunk("ageVerification/finalize", async (params, thunkAPI) => {
    try {
        const apiUrl = `${getBaseUrlVersioned(API_VERSION.V1)}/${API_PATH}/finalize`;
        const response = await axiosClient.post(apiUrl, params);
        return response.data;
    }
    catch (error) {
        throw thunkAPI.rejectWithValue(error?.response?.data);
    }
});
const initialState = {
    loading: false,
    loadingType: null,
    ageVerificationInitializeData: null,
    ageVerificationFinalizeData: null,
    ageVerificationStatusData: null,
    ageVerificationMethodsData: null,
};
const handlePending = (state, loadingType) => {
    state.loading = true;
    state.loadingType = loadingType;
};
const handleFulfilled = (state) => {
    state.loading = false;
    state.loadingType = null;
};
const handleRejected = (state) => {
    state.loading = false;
    state.loadingType = null;
};
export const ageVerificationSlice = createSlice({
    name: "ageVerification",
    initialState: initialState,
    reducers: {
        clearAgeVerificationLoading: (state) => {
            state.loading = false;
            state.loadingType = null;
        },
    },
    extraReducers: (builder) => {
        // age verification status
        builder.addCase(getAgeVerificationStatus.pending, (state) => {
            handlePending(state, AgeVerificationFlow.GET_AGE_VERIFICATION_STATUS);
        });
        builder.addCase(getAgeVerificationStatus.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.ageVerificationStatusData = action.payload;
        });
        builder.addCase(getAgeVerificationStatus.rejected, (state) => {
            handleRejected(state);
        });
        // age verification methods
        builder.addCase(getAgeVerificationMethods.pending, (state) => {
            handlePending(state, AgeVerificationFlow.GET_AGE_VERIFICATION_METHODS);
        });
        builder.addCase(getAgeVerificationMethods.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.ageVerificationMethodsData = action.payload;
        });
        builder.addCase(getAgeVerificationMethods.rejected, (state) => {
            handleRejected(state);
        });
        // age verification initialize
        builder.addCase(postAgeVerificationInitialize.pending, (state) => {
            handlePending(state, AgeVerificationFlow.POST_AGE_VERIFICATION_INITIALIZE);
        });
        builder.addCase(postAgeVerificationInitialize.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.ageVerificationInitializeData = action.payload;
        });
        builder.addCase(postAgeVerificationInitialize.rejected, (state) => {
            handleRejected(state);
        });
        builder.addCase(postAgeVerificationFinalize.pending, (state) => {
            handlePending(state, AgeVerificationFlow.POST_AGE_VERIFICATION_FINALIZE);
        });
        // age verification finalize
        builder.addCase(postAgeVerificationFinalize.fulfilled, (state, action) => {
            handleFulfilled(state);
            state.ageVerificationFinalizeData = action.payload;
        });
        builder.addCase(postAgeVerificationFinalize.rejected, (state) => {
            handleRejected(state);
        });
    },
});
export const { clearAgeVerificationLoading } = ageVerificationSlice.actions;
export default ageVerificationSlice.reducer;
